import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Loader2, Mail, Heart, PauseCircle, XCircle, CheckCircle2, LogOut, AlertTriangle } from "lucide-react";
import PublicHeader from "@/components/PublicHeader";
import PublicFooter from "@/components/PublicFooter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Textarea } from "@/components/ui/textarea";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useToast } from "@/hooks/use-toast";

// Module-level (not component-state) guard: this dev environment's React
// StrictMode double-invokes effects on mount, and since the magic-link
// token is single-use, a component-instance ref isn't reliable here - a
// module-level set survives across any remount and stops the token from
// ever being sent to /verify twice.
const attemptedTokens = new Set();

const STATUS_LABELS = {
  active: { label: "Active", className: "bg-amtm-success/15 text-amtm-success" },
  paused: { label: "Paused", className: "bg-amtm-textMuted/15 text-amtm-textMuted" },
  halted: { label: "Payment issue", className: "bg-amtm-error/15 text-amtm-error" },
  pending: { label: "Setting up", className: "bg-amtm-info/15 text-amtm-info" },
  authenticated: { label: "Setting up", className: "bg-amtm-info/15 text-amtm-info" },
  cancelled: { label: "Cancelled", className: "bg-amtm-textMuted/15 text-amtm-textMuted" },
  completed: { label: "Completed", className: "bg-amtm-textMuted/15 text-amtm-textMuted" },
};

function EmailLoginForm() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/guardian/portal/request-link", { email: email.trim() });
      setSent(true);
    } catch (err) {
      toast({ variant: "destructive", title: formatApiErrorDetail(err.response?.data?.detail) });
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <div className="text-center py-6" data-testid="magic-link-sent-message">
        <Mail className="h-10 w-10 text-amtm-teal mx-auto mb-4" />
        <p className="text-amtm-text font-semibold mb-1.5">Check your email</p>
        <p className="text-sm text-amtm-textMuted">
          If {email.trim()} is registered as a Guardian, we've sent a login link - it's valid for 15 minutes.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4" data-testid="guardian-portal-login-form">
      <div>
        <Label htmlFor="portal-email">Your email address</Label>
        <Input
          id="portal-email"
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@example.com"
          data-testid="guardian-portal-email-input"
          className="mt-1.5 h-12"
        />
        <p className="text-xs text-amtm-textMuted mt-1.5">
          The email you used when becoming a Guardian. We'll send you a one-time login link - no password needed.
        </p>
      </div>
      <Button type="submit" disabled={loading || !email.trim()} className="w-full h-12 bg-amtm-teal text-white hover:bg-amtm-teal/90 font-semibold" data-testid="guardian-portal-send-link-button">
        {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Send me a login link"}
      </Button>
    </form>
  );
}

function SubscriptionCard({ sub, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null); // null | "pause" | "cancel"
  const [reason, setReason] = useState("");
  const { toast } = useToast();
  const status = STATUS_LABELS[sub.status] || STATUS_LABELS.pending;

  const closeDialog = (open) => {
    if (!open) {
      setConfirmAction(null);
      setReason("");
    }
  };

  const runAction = async (action) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/guardian/portal/subscriptions/${sub.id}/${action}`, {
        reason: reason.trim() || null,
      });
      toast({ title: data.message });
      onChanged();
    } catch (err) {
      toast({ variant: "destructive", title: formatApiErrorDetail(err.response?.data?.detail) });
    } finally {
      setBusy(false);
      setConfirmAction(null);
      setReason("");
    }
  };

  return (
    <div className="bg-white border border-amtm-border rounded-2xl p-5" data-testid={`guardian-subscription-card-${sub.id}`}>
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <p className="font-display text-xl text-amtm-text">₹{sub.amount.toLocaleString("en-IN")}/month</p>
          <p className="text-xs text-amtm-textMuted mt-0.5">{sub.tier_name} Guardian &middot; {sub.total_count_charged || 0} charge{sub.total_count_charged === 1 ? "" : "s"} so far</p>
        </div>
        <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${status.className}`} data-testid={`guardian-subscription-status-${sub.id}`}>
          {status.label}
        </span>
      </div>

      {sub.status === "active" && (
        <div className="flex gap-2 mt-4">
          <Button size="sm" variant="outline" onClick={() => setConfirmAction("pause")} disabled={busy} data-testid={`pause-subscription-button-${sub.id}`}>
            <PauseCircle className="h-4 w-4 mr-1.5" /> Pause
          </Button>
          <Button size="sm" variant="outline" className="text-amtm-error border-amtm-error/30 hover:bg-amtm-error/5" onClick={() => setConfirmAction("cancel")} disabled={busy} data-testid={`cancel-subscription-button-${sub.id}`}>
            <XCircle className="h-4 w-4 mr-1.5" /> Cancel
          </Button>
        </div>
      )}
      {sub.status === "paused" && (
        <p className="text-xs text-amtm-textMuted mt-3">
          Paused - write to{" "}
          <a href="mailto:getinvolved@amtmindia.org" className="text-amtm-giveText underline">getinvolved@amtmindia.org</a> to resume anytime.
        </p>
      )}

      <AlertDialog open={!!confirmAction} onOpenChange={closeDialog}>
        <AlertDialogContent data-testid="guardian-action-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirmAction === "cancel" ? "Cancel your Guardian gift?" : "Pause your Guardian gift?"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirmAction === "cancel"
                ? "Your monthly gift will stop from your next billing cycle - no further charges will be made. You can always become a Guardian again later."
                : "We'll pause charges immediately. You can email us anytime to resume."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="px-1">
            <Label htmlFor="action-reason" className="text-xs text-amtm-textMuted">
              Tell us why (optional - totally fine to skip)
            </Label>
            <Textarea
              id="action-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. taking a break, financial reasons, moving to a different cause..."
              rows={2}
              className="mt-1.5 resize-none"
              data-testid="guardian-action-reason-input"
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="guardian-action-cancel-button">Never mind</AlertDialogCancel>
            <AlertDialogAction onClick={() => runAction(confirmAction)} disabled={busy} data-testid="guardian-action-confirm-button">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Yes, I'm sure"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function Dashboard({ data, onLoggedOut, onChanged }) {
  const { toast } = useToast();
  const handleLogout = async () => {
    try {
      await api.post("/guardian/portal/logout");
    } finally {
      onLoggedOut();
      toast({ title: "Logged out" });
    }
  };

  return (
    <div data-testid="guardian-portal-dashboard">
      <div className="flex items-start justify-between gap-4 mb-8">
        <div>
          <p className="text-sm text-amtm-textMuted">Welcome back,</p>
          <h1 className="font-display text-2xl md:text-3xl text-amtm-text">{data.donor.name}</h1>
        </div>
        <Button variant="outline" size="sm" onClick={handleLogout} data-testid="guardian-portal-logout-button">
          <LogOut className="h-4 w-4 mr-1.5" /> Log out
        </Button>
      </div>

      {data.subscriptions.length === 0 ? (
        <div className="text-center py-12 bg-white border border-amtm-border rounded-2xl" data-testid="guardian-portal-no-subscriptions">
          <Heart className="h-10 w-10 text-amtm-giveText mx-auto mb-4" />
          <p className="text-amtm-text font-semibold mb-1.5">No monthly gift yet</p>
          <p className="text-sm text-amtm-textMuted mb-4">You don't have an active Guardian subscription on this account.</p>
          <Button asChild className="bg-amtm-give text-amtm-giveText hover:bg-amtm-giveHover">
            <Link to="/guardians">Become a Guardian</Link>
          </Button>
        </div>
      ) : (
        <div className="space-y-4 mb-10">
          {data.subscriptions.map((sub) => (
            <SubscriptionCard key={sub.id} sub={sub} onChanged={onChanged} />
          ))}
        </div>
      )}

      {data.donations.length > 0 && (
        <div>
          <h2 className="font-display text-lg text-amtm-text mb-3">Your gifts</h2>
          <div className="bg-white border border-amtm-border rounded-2xl divide-y divide-amtm-border" data-testid="guardian-portal-donations-list">
            {data.donations.map((d) => (
              <div key={d.id} className="flex items-center justify-between px-4 py-3 text-sm" data-testid={`guardian-portal-donation-${d.id}`}>
                <div className="flex items-center gap-2 text-amtm-textMuted">
                  <CheckCircle2 className="h-4 w-4 text-amtm-success" />
                  {new Date(d.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
                </div>
                <span className="font-semibold text-amtm-text">₹{d.amount.toLocaleString("en-IN")}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function GuardianPortal() {
  const [data, setData] = useState(null); // undefined-ish states handled via null + loading flag
  const [loading, setLoading] = useState(true);
  const [verifyError, setVerifyError] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();

  const fetchMe = () => {
    setLoading(true);
    api
      .get("/guardian/portal/me")
      .then((res) => setData(res.data))
      .catch(() => setData(false))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    const token = searchParams.get("token");
    if (!token) {
      fetchMe();
      return;
    }
    if (attemptedTokens.has(token)) return;
    attemptedTokens.add(token);
    // Magic-link verification: exchange the one-time token for a donor
    // session cookie, then drop ?token from the URL (same route, no
    // navigation) so a refresh - or the token being reused - can never
    // re-trigger verification.
    api
      .post("/guardian/portal/verify", { token })
      .then(() => {
        setSearchParams({}, { replace: true });
        fetchMe();
      })
      .catch((err) => {
        setSearchParams({}, { replace: true });
        setVerifyError(formatApiErrorDetail(err.response?.data?.detail) || "This login link is invalid or has expired.");
        setData(false);
        setLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen bg-amtm-bg" data-testid="guardian-portal-page">
      <PublicHeader />
      <div className="max-w-2xl mx-auto px-6 md:px-10 py-16 md:py-20">
        {loading ? (
          <div className="flex justify-center py-16" data-testid="guardian-portal-loading">
            <Loader2 className="h-6 w-6 animate-spin text-amtm-giveText" />
          </div>
        ) : data ? (
          <Dashboard data={data} onLoggedOut={() => setData(false)} onChanged={fetchMe} />
        ) : (
          <div className="bg-white border border-amtm-border rounded-2xl p-8">
            <p className="text-xs md:text-sm font-semibold uppercase tracking-[0.18em] text-amtm-giveText mb-4">
              Guardian Portal
            </p>
            <h1 className="font-display text-2xl md:text-3xl text-amtm-text mb-2">Manage your monthly gift</h1>
            <p className="text-sm text-amtm-textMuted mb-6">View, pause, or cancel your Guardian subscription anytime.</p>
            {verifyError && (
              <div
                className="flex items-start gap-2.5 bg-amtm-error/10 text-amtm-error rounded-xl px-4 py-3 mb-6 text-sm"
                data-testid="guardian-portal-verify-error"
              >
                <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                <span>{verifyError}</span>
              </div>
            )}
            <EmailLoginForm />
          </div>
        )}
      </div>
      <PublicFooter />
    </div>
  );
}
